import React, { useState } from "react";
import {
    Box, Checkbox, Chip, Collapse, IconButton, ListSubheader,
    MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowUpwardRoundedIcon   from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import TuneRoundedIcon          from "@mui/icons-material/TuneRounded";
import WarningAmberRoundedIcon  from "@mui/icons-material/WarningAmberRounded";
import { FIELD_TYPES, NEEDS_OPTIONS, groupSlots, groupFieldTypes } from "./formAdminConstants";

/**
 * 입력칸 한 줄.
 *
 * 접힌 상태에서는 이름·종류·필수·슬롯만 보인다. 관리자가 실제로 자주 만지는 게 그 넷이고,
 * 나머지(저장 키·안내·선택지·표 열)는 펼쳐야 나온다.
 *
 * <p><b>슬롯</b>이 이 화면에서 제일 중요한 개념이다. 라벨은 자유롭게 바꿔도 되지만 슬롯이
 * 붙어 있어야 재무·인사가 그 칸의 값을 찾아간다. 라벨을 «지급예정일» → «송금일» 로 바꿔도
 * 슬롯이 그대로면 자금수지는 계속 동작한다.
 */
export default function FieldRow({
    field, index, total, slots, slotOwner, isNew,
    onChange, onMove, onRemove,
}) {
    const [open, setOpen] = useState(false);
    const set = (patch) => onChange({ ...field, ...patch });

    const needsOptions = NEEDS_OPTIONS.includes(field.type);
    const optionsEmpty = needsOptions && !(field.options || []).length;
    const gridEmpty    = field.type === "GRID" && !(field.columns || []).length;
    // 같은 슬롯을 두 칸이 물면 서버는 «위쪽 칸» 을 쓴다. 아래쪽 칸에만 경고를 띄운다.
    const dupSlot      = field.slot && slotOwner && slotOwner.key !== field.key;

    return (
        <Box sx={{ border: "1px solid", borderColor: open ? "#bfdbfe" : "#e2e8f0",
                   borderRadius: 1.5, mb: 0.75, bgcolor: open ? "#f8fbff" : "#fff" }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ p: 0.75 }}>
                <Stack sx={{ flexShrink: 0 }}>
                    <IconButton size="small" disabled={index === 0}
                                onClick={() => onMove(index, index - 1)} sx={{ p: 0.15 }}>
                        <ArrowUpwardRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" disabled={index === total - 1}
                                onClick={() => onMove(index, index + 1)} sx={{ p: 0.15 }}>
                        <ArrowDownwardRoundedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Stack>

                <TextField
                    size="small" value={field.label || ""} placeholder="칸 이름"
                    onChange={(e) => set({ label: e.target.value })}
                    sx={{ flex: 1, minWidth: 120 }}
                />

                <TextField
                    size="small" select value={field.type || "TEXT"}
                    onChange={(e) => set({ type: e.target.value })}
                    sx={{ width: 132, flexShrink: 0 }}
                >
                    {groupFieldTypes().flatMap((g) => [
                        <ListSubheader key={`t-${g.title}`}
                            sx={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", lineHeight: 2.2 }}>
                            {g.title}
                        </ListSubheader>,
                        ...g.types.map((t) => (
                            <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13, pl: 2.5 }}>{t.label}</MenuItem>
                        )),
                    ])}
                </TextField>

                <TextField
                    size="small" select value={field.slot || ""}
                    onChange={(e) => set({ slot: e.target.value || null })}
                    sx={{ width: 168, flexShrink: 0 }}
                    SelectProps={{ displayEmpty: true, renderValue: (v) =>
                        v ? (slots.find((s) => s.code === v)?.label || v)
                          : <span style={{ color: "#cbd5e1" }}>연동 안 함</span> }}
                >
                    <MenuItem value="" sx={{ fontSize: 13, color: "#94a3b8" }}>연동 안 함</MenuItem>
                    {groupSlots(slots).flatMap((g) => [
                        <ListSubheader key={`h-${g.title}`}
                            sx={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", lineHeight: 2.2 }}>
                            {g.title}
                        </ListSubheader>,
                        ...g.slots.map((s) => (
                            <MenuItem key={s.code} value={s.code} sx={{ fontSize: 13, pl: 2.5 }}>{s.label}</MenuItem>
                        )),
                    ])}
                </TextField>

                <Tooltip title="필수 입력">
                    <Checkbox size="small" checked={!!field.required}
                              onChange={(e) => set({ required: e.target.checked })} />
                </Tooltip>

                <IconButton size="small" onClick={() => setOpen((v) => !v)}
                            sx={{ color: open ? "#2563eb" : "#94a3b8" }}>
                    <TuneRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
                <IconButton size="small" onClick={() => onRemove(index)} sx={{ color: "#ef4444" }}>
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
            </Stack>

            {(optionsEmpty || gridEmpty || dupSlot) && (
                <Stack direction="row" alignItems="center" spacing={0.5}
                       sx={{ px: 1.25, pb: 0.75, color: "#b45309" }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: 11 }}>
                        {dupSlot     && `이 슬롯은 위쪽 «${slotOwner.label}» 칸이 이미 쓰고 있습니다 — 이 칸의 값은 무시됩니다.`}
                        {optionsEmpty && "선택지가 비어 있어 상신 화면에서 고를 게 없습니다."}
                        {gridEmpty    && "표에 열이 없습니다."}
                    </Typography>
                </Stack>
            )}

            <Collapse in={open}>
                <Box sx={{ px: 1.25, pb: 1.25, pt: 0.25, borderTop: "1px dashed #e2e8f0" }}>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {/* 저장 키는 못 고친다. formData JSON 의 실제 열쇠라 바꾸면 이미 올라간 문서가
                            이 칸의 값을 영영 못 찾는다. 되돌릴 방법도 없다 — 아예 잠근다. */}
                        <TextField
                            size="small" label="저장 키" value={field.key || ""}
                            disabled
                            sx={{ width: 180 }}
                            helperText="칸을 만들 때 정해지며 바꿀 수 없습니다"
                            FormHelperTextProps={{ sx: { fontSize: 10, color: "#94a3b8", m: 0, mt: 0.25 } }}
                        />
                        <TextField
                            size="small" label="기본값" value={field.defaultValue || ""}
                            onChange={(e) => set({ defaultValue: e.target.value })}
                            sx={{ width: 160 }}
                            helperText={field.type === "HIDDEN" && field.required ? "숨김+필수는 기본값이 있어야 합니다" : ""}
                            FormHelperTextProps={{ sx: { fontSize: 10, color: "#b45309", m: 0, mt: 0.25 } }}
                        />
                        <TextField
                            size="small" label="입력 도움말" value={field.help || ""}
                            onChange={(e) => set({ help: e.target.value })}
                            sx={{ flex: 1 }}
                        />
                    </Stack>

                    {needsOptions && (
                        <OptionEditor
                            options={field.options || []}
                            onChange={(options) => set({ options })}
                        />
                    )}

                    {field.type === "GRID" && (
                        <ColumnEditor
                            columns={field.columns || []}
                            slots={slots}
                            onChange={(columns) => set({ columns })}
                        />
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}

/** 선택지 — 쉼표로 나눠 넣게 한다. 한 줄에 하나씩 추가하는 UI 보다 붙여넣기가 빠르다. */
function OptionEditor({ options, onChange }) {
    return (
        <Box sx={{ mt: 1 }}>
            <TextField
                size="small" fullWidth label="선택지 (쉼표로 구분)"
                value={options.join(", ")}
                onChange={(e) => onChange(
                    e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            />
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
                {options.map((o) => <Chip key={o} label={o} size="small" sx={{ fontSize: 11 }} />)}
            </Stack>
        </Box>
    );
}

/** 표의 열 — 이름·종류·슬롯만. 열 안에 다시 표를 넣는 건 막는다. */
function ColumnEditor({ columns, slots, onChange }) {
    const setAt = (i, patch) => onChange(columns.map((c, j) => (j === i ? { ...c, ...patch } : c)));

    return (
        <Box sx={{ mt: 1.25 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", mb: 0.5 }}>표의 열</Typography>
            {columns.map((c, i) => (
                <Stack key={i} direction="row" spacing={0.75} sx={{ mb: 0.5 }}>
                    <TextField size="small" placeholder="열 이름" value={c.label || ""}
                               onChange={(e) => setAt(i, { label: e.target.value })} sx={{ flex: 1 }} />
                    <TextField size="small" select value={c.type || "TEXT"}
                               onChange={(e) => setAt(i, { type: e.target.value })} sx={{ width: 128 }}>
                        {FIELD_TYPES.filter((t) => t.value !== "GRID").map((t) => (
                            <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13 }}>{t.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField size="small" select value={c.slot || ""}
                               onChange={(e) => setAt(i, { slot: e.target.value || null })}
                               sx={{ width: 150 }}
                               SelectProps={{ displayEmpty: true, renderValue: (v) =>
                                   v ? (slots.find((s) => s.code === v)?.label || v)
                                     : <span style={{ color: "#cbd5e1" }}>연동 안 함</span> }}>
                        <MenuItem value="" sx={{ fontSize: 13, color: "#94a3b8" }}>연동 안 함</MenuItem>
                        {slots.map((s) => (
                            <MenuItem key={s.code} value={s.code} sx={{ fontSize: 13 }}>{s.label}</MenuItem>
                        ))}
                    </TextField>
                    <IconButton size="small" sx={{ color: "#ef4444" }}
                                onClick={() => onChange(columns.filter((_, j) => j !== i))}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Stack>
            ))}
            <Box onClick={() => onChange([...columns,
                    { key: `col${columns.length + 1}`, label: "", type: "TEXT" }])}
                 sx={{ fontSize: 12, color: "#2563eb", cursor: "pointer", fontWeight: 700, mt: 0.25 }}>
                + 열 추가
            </Box>
        </Box>
    );
}
